import type { SavedViewFilters, SavedViewSort } from '../savedViews/types';
import type { Task } from '../tasks/types';

/**
 * Filtering and sorting for the List View toolbar (handoff 25), as pure
 * functions over the already-loaded task list. Everything runs client-side:
 * the list a project shows is already in memory, so a filter is a lens, not
 * a request.
 *
 * The filter and sort shapes are deliberately the saved-view ones
 * (SavedViewFilters, SavedViewSort from Stage 3): what the toolbar holds is
 * exactly what a saved view stores, so Stage 7 saves and restores toolbar
 * state without translation.
 *
 * Section joined in Phase 5: sections stay the visible grouping in List
 * View, but on the Board they are filters (handoff 18), so the toolbar
 * offers one for both views. Deliberately absent: has-attachments and
 * supplier-review status (Phase 6 brings the supplier branch).
 */

/** Extra per-task facts the row indicators already computed. */
export interface TaskFilterContext {
  commentCounts: Record<string, number>;
  subtaskCounts: Record<string, number>;
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function dueMatches(
  task: Task,
  bucket: NonNullable<SavedViewFilters['dueWithin']>,
  today: Date,
): boolean {
  if (bucket === 'none') {
    return task.due_date === null;
  }
  if (task.due_date === null) {
    return false;
  }
  const due = startOfDay(new Date(`${task.due_date}T00:00:00`));
  if (Number.isNaN(due.getTime())) {
    return false;
  }
  const base = startOfDay(today);
  const dayMs = 24 * 60 * 60 * 1000;
  const diffDays = Math.round((due.getTime() - base.getTime()) / dayMs);

  switch (bucket) {
    case 'overdue':
      // A finished task stops appearing as overdue (handoff 22).
      return diffDays < 0 && !task.is_completed;
    case 'today':
      return diffDays === 0;
    case 'tomorrow':
      return diffDays === 1;
    case 'this_week':
      return diffDays >= 0 && diffDays <= 6;
  }
}

/**
 * The one search the Hub has for a task, shared so My Day and the List View
 * agree on what a query hits: title, description and remarks, case-blind.
 */
export function taskMatchesSearch(task: Task, query: string): boolean {
  return searchMatches(task, query);
}

function searchMatches(task: Task, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === '') {
    return true;
  }
  return [task.title, task.description ?? '', task.remarks ?? ''].some((field) =>
    field.toLowerCase().includes(needle),
  );
}

/**
 * True when the task passes every set filter. Absent fields filter nothing;
 * assigneeId null means "unassigned" as a real choice.
 */
export function taskMatchesFilters(
  task: Task,
  filters: SavedViewFilters,
  context: TaskFilterContext,
  today: Date = new Date(),
): boolean {
  if (filters.search !== undefined && !searchMatches(task, filters.search)) {
    return false;
  }
  if (filters.assigneeId !== undefined && task.assignee_id !== filters.assigneeId) {
    return false;
  }
  if (filters.statusId !== undefined && task.status_id !== filters.statusId) {
    return false;
  }
  if (filters.sectionId !== undefined && task.section_id !== filters.sectionId) {
    return false;
  }
  if (filters.priority !== undefined && task.priority !== filters.priority) {
    return false;
  }
  if (filters.isCompleted !== undefined && task.is_completed !== filters.isCompleted) {
    return false;
  }
  if (filters.dueWithin !== undefined && !dueMatches(task, filters.dueWithin, today)) {
    return false;
  }
  if (filters.hasComments !== undefined) {
    const has = (context.commentCounts[task.id] ?? 0) > 0;
    if (has !== filters.hasComments) {
      return false;
    }
  }
  if (filters.hasSubtasks !== undefined) {
    const has = (context.subtaskCounts[task.id] ?? 0) > 0;
    if (has !== filters.hasSubtasks) {
      return false;
    }
  }
  return true;
}

export function applyTaskFilters(
  tasks: Task[],
  filters: SavedViewFilters,
  context: TaskFilterContext,
  today: Date = new Date(),
): Task[] {
  return tasks.filter((task) => taskMatchesFilters(task, filters, context, today));
}

/** True when any filter is set at all, for the summary line and Clear button. */
export function hasActiveFilters(filters: SavedViewFilters): boolean {
  return Object.keys(filters).length > 0;
}

const PRIORITY_RANK = { high: 0, medium: 1, low: 2 } as const;

/**
 * Orders tasks for display. Position is the hand-arranged default; the rest
 * are stable rearrangements of it (ties keep position order because the sort
 * compares equal and Array.prototype.sort is stable). Null values — no due
 * date, no priority — sort last in either direction: absence is not a rank.
 */
export function applyTaskSort(tasks: Task[], sort: SavedViewSort): Task[] {
  const column = sort.column ?? 'position';
  const direction = sort.direction === 'desc' ? -1 : 1;
  const ordered = tasks.slice().sort((left, right) => left.position - right.position);

  if (column === 'position') {
    return direction === 1 ? ordered : ordered.reverse();
  }

  function compare(left: Task, right: Task): number {
    switch (column) {
      case 'title':
        return left.title.localeCompare(right.title, undefined, { sensitivity: 'base' });
      case 'created_at':
        return left.created_at.localeCompare(right.created_at);
      case 'due_date': {
        if (left.due_date === right.due_date) {
          return 0;
        }
        if (left.due_date === null) {
          return 1 * direction; // stays last after the direction flip below
        }
        if (right.due_date === null) {
          return -1 * direction;
        }
        return left.due_date.localeCompare(right.due_date);
      }
      case 'priority': {
        const leftRank = left.priority === null ? Infinity : PRIORITY_RANK[left.priority];
        const rightRank = right.priority === null ? Infinity : PRIORITY_RANK[right.priority];
        if (leftRank === rightRank) {
          return 0;
        }
        if (leftRank === Infinity) {
          return 1 * direction;
        }
        if (rightRank === Infinity) {
          return -1 * direction;
        }
        return leftRank < rightRank ? -1 : 1;
      }
      default:
        return 0;
    }
  }

  return ordered.sort((left, right) => compare(left, right) * direction);
}
